import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, MessageSquareQuote, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";

export function Stars({ value, size = "w-4 h-4", className = "" }) {
  return (
    <span className={`inline-flex gap-0.5 ${className}`} aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`${size} ${n <= value ? "fill-amber-400 text-amber-400" : "text-white/30"}`} />)}
    </span>
  );
}

export function ReviewsBand() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [mine, setMine] = useState(null);
  const loadPublic = () => api.get("/reviews/public").then((r) => setData(r.data)).catch(() => setData({ items: [], count: 0 }));
  const loadMine = () => (user ? api.get("/reviews/mine").then((r) => setMine(r.data.review)).catch(() => {}) : setMine(null));
  useEffect(() => { loadPublic(); }, []);
  useEffect(() => { loadMine(); }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const items = data?.items || [];
  const MINE_COPY = { pending: "Your review was received and is waiting for our team's approval.", approved: "Your review is published below.", rejected: "Your review was reviewed but not published." };
  return (
    <div className="bg-primary relative overflow-hidden" data-testid="reviews-band">
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #fff 0, transparent 40%), radial-gradient(circle at 80% 80%, #fff 0, transparent 40%)" }} aria-hidden />
      <div className="relative max-w-6xl mx-auto px-4 py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/70 flex items-center gap-2"><MessageSquareQuote className="w-4 h-4" /> Candidate reviews</p>
            <h2 className="mt-2 font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">What candidates say</h2>
            {data?.count > 0 ? (
              <p className="mt-2 text-white/90 flex items-center gap-2" data-testid="reviews-summary"><Stars value={Math.round(data.average)} /> <b>{data.average}</b> / 5 · {data.count} review{data.count === 1 ? "" : "s"} from First Step candidates</p>
            ) : (
              <p className="mt-2 text-white/85 max-w-xl" data-testid="reviews-empty">Real reviews from real candidates — every one is written by a First Step user and checked by our team before it appears here. None published yet; be the first.</p>
            )}
          </div>
          <div className="flex flex-col items-start md:items-end gap-2 shrink-0">
            <Button onClick={() => (user ? setOpen(true) : navigate("/auth?mode=register"))} size="lg" className="bg-white text-primary hover:bg-slate-100 h-12 px-6 font-semibold" data-testid="reviews-leave-button">
              {user ? (mine ? "Update your review" : "Leave a review") : "Start free & leave a review"}
            </Button>
            {mine && <p className="text-xs text-white/85" data-testid={`reviews-mine-status-${mine.status}`}>{MINE_COPY[mine.status] || ""}</p>}
          </div>
        </div>
        {items.length > 0 && (
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="reviews-grid">
            {items.slice(0, 6).map((r, i) => (
              <figure key={r.id} className="rounded-2xl bg-white/10 backdrop-blur border border-white/15 p-5 text-white flex flex-col" style={{ animationDelay: `${i * 60}ms` }} data-testid={`review-card-${r.id}`}>
                <Stars value={r.rating} />
                <blockquote className="mt-3 text-sm leading-relaxed text-white/95 flex-1">“{r.text}”</blockquote>
                <figcaption className="mt-4 text-xs text-white/75"><b className="text-white">{r.name}</b>{r.role && <> · {r.role}</>}</figcaption>
              </figure>
            ))}
          </div>
        )}
      </div>
      <ReviewDialog open={open} onClose={() => setOpen(false)} onSubmitted={(r) => { setMine(r); loadPublic(); }} />
    </div>
  );
}

export function ReviewDialog({ open, onClose, onSubmitted }) {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [role, setRole] = useState("");
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get("/reviews/mine").then(({ data }) => { if (data.review) { setMine(data.review); setRating(data.review.rating); setText(data.review.text); setRole(data.review.role || ""); } }).catch(() => {});
  }, [open]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/reviews", { rating, text, role });
      toast.success(data.message); onSubmitted?.(data.review); onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit."); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="review-dialog">
        <DialogTitle className="font-heading text-2xl">{mine ? "Update your review" : "Leave a review"}</DialogTitle>
        <DialogDescription>Honest and specific helps other candidates most. Reviews are read by our team before they're published{mine && mine.status !== "approved" ? ` — yours is currently ${mine.status}.` : "."}</DialogDescription>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="flex items-center gap-1" data-testid="review-rating">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => setRating(n)} aria-label={`${n} star${n > 1 ? "s" : ""}`} className="p-0.5" data-testid={`review-star-${n}`}>
                <Star className={`w-7 h-7 transition-colors ${n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-300"}`} />
              </button>
            ))}
          </div>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} minLength={20} maxLength={400} rows={5} required placeholder="What has First Step helped you with? What should other candidates know?" data-testid="review-text" />
          <p className="text-[11px] text-slate-400 -mt-2 text-right">{text.length}/400</p>
          <Input value={role} onChange={(e) => setRole(e.target.value)} maxLength={60} placeholder="Where you are (optional) — e.g. EMT, Firefighter I candidate" data-testid="review-role" />
          <Button type="submit" disabled={busy || text.trim().length < 20} className="w-full bg-primary hover:bg-primary/90 font-semibold" data-testid="review-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : mine ? "Update review" : "Submit review"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
